import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Search, Receipt } from "lucide-react";
import InvoiceRowActions from "@/components/invoice/InvoiceRowActions";
import PeppolModeBanner from "@/components/invoice/PeppolModeBanner";

const STATUS_META = {
  // Phase Semi-Auto Facture (12/09/2026) — nouveaux statuts alignés cycle produit
  a_envoyer: { label: "À envoyer", cls: "badge-info" },
  envoyee: { label: "Envoyée", cls: "badge-warning" },
  paid: { label: "Payée", cls: "badge-success" },
  overdue: { label: "En retard", cls: "badge-error" },
};

const TYPE_META = {
  acompte: { label: "Acompte", cls: "badge-neutral" },
  solde: { label: "Solde", cls: "badge-neutral" },
};

const PEPPOL_META = {
  pending: { label: "Peppol · en attente", cls: "badge-warning" },
  sent: { label: "Peppol · envoyée", cls: "badge-info" },
  acknowledged: { label: "Peppol · reçue", cls: "badge-success" },
  failed: { label: "Peppol · échec", cls: "badge-error" },
  // Commit 5 (05/10/2026) — statut dédié mock
  test_simulated: { label: "Peppol · SIMULÉ", cls: "badge-warning" },
};

function fmtEur(n) {
  return new Intl.NumberFormat("fr-BE", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
  }).format(Number(n || 0));
}

function fmtDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("fr-BE");
}

export default function InvoicesListPage() {
  const navigate = useNavigate();
  // Phase γ-2 (12/09/2026) — Support du query param `?status=` pour permettre
  // au widget dashboard `widget-invoices-to-send` de pointer vers la liste filtrée.
  const [searchParams, setSearchParams] = useSearchParams();
  const initialStatus = searchParams.get("status");
  const validInitialStatus = initialStatus && ["a_envoyer", "envoyee", "paid", "overdue"].includes(initialStatus)
    ? initialStatus
    : "__all__";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(validInitialStatus);
  const [type, setType] = useState("__all__");
  const [search, setSearch] = useState("");

  // Sync status state → URL (?status=…) pour permettre le partage de lien filtré
  useEffect(() => {
    const current = searchParams.get("status");
    if (status === "__all__" && current) {
      const next = new URLSearchParams(searchParams);
      next.delete("status");
      setSearchParams(next, { replace: true });
    } else if (status !== "__all__" && current !== status) {
      const next = new URLSearchParams(searchParams);
      next.set("status", status);
      setSearchParams(next, { replace: true });
    }
  }, [status]);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (status !== "__all__") params.append("status", status);
      if (type !== "__all__") params.append("type", type);
      // tri par échéance quand on regarde les impayées
      if (status === "envoyee" || status === "overdue") params.append("sort_by_due", "true");
      const { data } = await api.get(`/invoices?${params.toString()}`);
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [status, type]);

  const filtered = items.filter((i) => {
    if (!search.trim()) return true;
    const s = search.trim().toLowerCase();
    return (
      (i.number || "").toLowerCase().includes(s) ||
      (i.client_display || "").toLowerCase().includes(s) ||
      (i.structured_communication || "").toLowerCase().includes(s)
    );
  });

  return (
    <div className="space-y-6" data-testid="invoices-list-page">
      <PeppolModeBanner />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="inline-flex items-center gap-2 text-2xl font-bold">
            <Receipt className="h-5 w-5 text-[#C4553B]" /> Factures
          </h1>
          <p className="text-sm text-slate-400">
            Toutes vos factures émises (acomptes + soldes). L&apos;émission d&apos;une facture se déclenche automatiquement à la signature du devis ou depuis la fiche chantier.
          </p>
        </div>
      </div>

      <Card className="border-slate-800 bg-slate-900/40">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-slate-500" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher (numéro, client, communication)"
                className="border-slate-700 bg-slate-950/40 pl-9 text-slate-100"
                data-testid="invoices-search-input"
              />
            </div>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="w-[170px] border-slate-700 bg-slate-950/40" data-testid="invoices-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="border-slate-700 bg-slate-900">
                <SelectItem value="__all__">Tous les statuts</SelectItem>
                {Object.entries(STATUS_META).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger className="w-[150px] border-slate-700 bg-slate-950/40" data-testid="invoices-type-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="border-slate-700 bg-slate-900">
                <SelectItem value="__all__">Tous les types</SelectItem>
                {Object.entries(TYPE_META).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-800 bg-slate-900/40">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-10" data-testid="invoices-loading">
              <Loader2 className="h-5 w-5 animate-spin text-slate-500" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-16 text-center text-sm text-slate-500" data-testid="invoices-empty">
              Aucune facture pour ces filtres. Émettez une facture depuis la fiche chantier.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-slate-800 bg-slate-950/40 text-left text-xs uppercase tracking-wider text-slate-400">
                  <tr>
                    <th className="px-4 py-2.5">Numéro</th>
                    <th className="px-4 py-2.5">Type</th>
                    <th className="px-4 py-2.5">Statut</th>
                    <th className="px-4 py-2.5">Client</th>
                    <th className="px-4 py-2.5">Émise le</th>
                    <th className="px-4 py-2.5">Échéance</th>
                    <th className="px-4 py-2.5 text-right">TVAC</th>
                    <th className="px-4 py-2.5">Peppol</th>
                    <th className="px-4 py-2.5 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((inv) => {
                    const meta = STATUS_META[inv.status] || STATUS_META.a_envoyer;
                    const tmeta = TYPE_META[inv.type] || TYPE_META.acompte;
                    const pmeta = inv.peppol_status ? PEPPOL_META[inv.peppol_status] : null;
                    return (
                      <tr
                        key={inv.id}
                        onClick={() => navigate(`/chantiers/${inv.chantier_id}`)}
                        className="cursor-pointer border-b border-slate-800/60 hover:bg-slate-950/40"
                        data-testid={`invoice-row-${inv.id}`}
                      >
                        <td className="px-4 py-2.5 font-mono text-slate-200">{inv.number}</td>
                        <td className="px-4 py-2.5">
                          <Badge className={`border ${tmeta.cls}`}>{tmeta.label}</Badge>
                        </td>
                        <td className="px-4 py-2.5">
                          <div className="flex items-center gap-1.5">
                            <Badge className={`border ${meta.cls}`}>{meta.label}</Badge>
                            {/* Phase Semi-Auto (12/09/2026) — badge "Signé" : cette facture
                                correspond à un devis parent signé. Toutes les factures émises
                                par le système le sont post-signage, ce badge est donc vrai par
                                construction. Vue d'ensemble rassurante avant clic envoi. */}
                            <Badge
                              className="border border-emerald-500/40 bg-emerald-500/10 text-emerald-400"
                              data-testid={`invoice-signed-badge-${inv.id}`}
                              title="Cette facture correspond à un devis parent signé par le client."
                            >
                              ✓ Devis signé
                            </Badge>
                          </div>
                        </td>
                        <td className="px-4 py-2.5 text-slate-200">{inv.client_display}</td>
                        <td className="px-4 py-2.5 text-slate-400">{fmtDate(inv.issued_at)}</td>
                        <td className="px-4 py-2.5 text-slate-400">{fmtDate(inv.due_at)}</td>
                        <td className="tabular px-4 py-2.5 text-right font-semibold text-slate-100">
                          {fmtEur(inv.amount_tvac)}
                        </td>
                        <td className="px-4 py-2.5">
                          {pmeta ? (
                            <Badge className={`border ${pmeta.cls}`}>{pmeta.label}</Badge>
                          ) : (
                            <span className="text-xs text-slate-500">—</span>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          <InvoiceRowActions invoice={inv} onChanged={load} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
